import { linearMeasurementLabelLayout } from '../measurementLabelLayout';

const annotationControlPoints = (annotation) => {
  if (annotation?.position) return [annotation.position];
  if (annotation?.points?.a && annotation?.points?.b) return [annotation.points.a, annotation.points.b];
  if (Array.isArray(annotation?.points)) return annotation.points;
  return [];
};

const constrainedDragDelta = (annotation, dx, dy) => {
  const points = annotationControlPoints(annotation).filter(
    point => Number.isFinite(point?.x) && Number.isFinite(point?.y)
  );
  if (!points.length) return { dx, dy };
  const minX = Math.min(...points.map(point => point.x));
  const maxX = Math.max(...points.map(point => point.x));
  const minY = Math.min(...points.map(point => point.y));
  const maxY = Math.max(...points.map(point => point.y));
  return {
    dx: Math.max(-minX, Math.min(1 - maxX, dx)),
    dy: Math.max(-minY, Math.min(1 - maxY, dy)),
  };
};

const movePoint = (point, dx, dy) => ({
  x: point.x + dx,
  y: point.y + dy,
});

export const moveAnnotationBy = (annotation, dx, dy) => {
  const constrained = constrainedDragDelta(annotation, dx, dy);
  if (annotation.position) {
    return { ...annotation, position: movePoint(annotation.position, constrained.dx, constrained.dy) };
  }
  if (annotation.points?.a && annotation.points?.b) {
    return {
      ...annotation,
      points: {
        a: movePoint(annotation.points.a, constrained.dx, constrained.dy),
        b: movePoint(annotation.points.b, constrained.dx, constrained.dy),
      },
    };
  }
  if (Array.isArray(annotation.points)) {
    return {
      ...annotation,
      points: annotation.points.map(point => movePoint(point, constrained.dx, constrained.dy)),
    };
  }
  return annotation;
};

const distanceToSegment = (point, start, end) => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;
  if (!lengthSquared) return Math.hypot(point.x - start.x, point.y - start.y);
  const t = Math.max(0, Math.min(1, ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared));
  return Math.hypot(point.x - (start.x + t * dx), point.y - (start.y + t * dy));
};

const angleDegrees = (points) => {
  if (!Array.isArray(points) || points.length < 3) return null;
  const [a, b, c] = points;
  const first = { x: a.x - b.x, y: a.y - b.y };
  const second = { x: c.x - b.x, y: c.y - b.y };
  const denominator = Math.hypot(first.x, first.y) * Math.hypot(second.x, second.y);
  if (!denominator) return null;
  const cosine = Math.max(-1, Math.min(1, (first.x * second.x + first.y * second.y) / denominator));
  return Math.acos(cosine) * 180 / Math.PI;
};

const cobbDegrees = (points) => {
  if (!Array.isArray(points) || points.length < 4) return null;
  const [a, b, c, d] = points;
  const first = Math.atan2(b.y - a.y, b.x - a.x);
  const second = Math.atan2(d.y - c.y, d.x - c.x);
  let degrees = Math.abs((second - first) * 180 / Math.PI);
  while (degrees > 180) degrees -= 180;
  return degrees > 90 ? 180 - degrees : degrees;
};

const renderedLabel = (annotation) => {
  if (annotation.type === 'text' || annotation.type === 'level') {
    if (!annotation.position) return null;
    return {
      anchor: annotation.position,
      text: annotation.type === 'level' ? `⬥ ${annotation.text || ''}` : String(annotation.text || ''),
      offsetX: 4,
      offsetY: -4,
    };
  }
  if ((annotation.showLabels ?? true) && annotation.type === 'angle') {
    const degrees = angleDegrees(annotation.points);
    if (degrees == null) return null;
    return {
      anchor: annotation.points[1],
      text: `${degrees.toFixed(1)}°`,
      offsetX: 6,
      offsetY: -6,
    };
  }
  if ((annotation.showLabels ?? true) && annotation.type === 'cobb') {
    const degrees = cobbDegrees(annotation.points);
    if (degrees == null) return null;
    const anchor = annotation.points.reduce(
      (total, point) => ({ x: total.x + point.x, y: total.y + point.y }),
      { x: 0, y: 0 }
    );
    anchor.x /= annotation.points.length;
    anchor.y /= annotation.points.length;
    return {
      anchor,
      text: `Cobb ${degrees.toFixed(1)}°`,
      offsetX: 6,
      offsetY: -6,
    };
  }
  return null;
};

const textLabelHit = (annotation, point, imageRect, defaultFontSize, tolerancePx) => {
  const label = renderedLabel(annotation);
  if (!label || !imageRect?.width || !imageRect?.height) return false;
  const fontSize = Number(['angle', 'cobb'].includes(annotation.type) ? annotation.labelFontSize : annotation.fontSize) || defaultFontSize;
  const anchorX = label.anchor.x * imageRect.width;
  const anchorY = label.anchor.y * imageRect.height;
  const pointX = point.x * imageRect.width;
  const pointY = point.y * imageRect.height;
  const estimatedWidth = Math.max(fontSize, label.text.length * fontSize * 0.62);
  const left = anchorX + label.offsetX - tolerancePx;
  const right = anchorX + label.offsetX + estimatedWidth + tolerancePx;
  const top = anchorY + label.offsetY - fontSize - tolerancePx;
  const bottom = anchorY + label.offsetY + fontSize * 0.3 + tolerancePx;

  return pointX >= left && pointX <= right && pointY >= top && pointY <= bottom;
};

const rectangleEdgeHit = (point, first, second, threshold) => {
  const left = Math.min(first.x, second.x);
  const right = Math.max(first.x, second.x);
  const top = Math.min(first.y, second.y);
  const bottom = Math.max(first.y, second.y);
  return [
    [{ x: left, y: top }, { x: right, y: top }],
    [{ x: right, y: top }, { x: right, y: bottom }],
    [{ x: right, y: bottom }, { x: left, y: bottom }],
    [{ x: left, y: bottom }, { x: left, y: top }],
  ].some(([start, end]) => distanceToSegment(point, start, end) <= threshold);
};

const ellipseEdgeHit = (point, first, second, threshold) => {
  const cx = (first.x + second.x) / 2;
  const cy = (first.y + second.y) / 2;
  const rx = Math.abs(second.x - first.x) / 2;
  const ry = Math.abs(second.y - first.y) / 2;
  if (!rx || !ry) return false;
  const normalizedRadius = Math.sqrt(((point.x - cx) / rx) ** 2 + ((point.y - cy) / ry) ** 2);
  const normalizedTolerance = threshold / Math.max(Math.min(rx, ry), threshold);
  return Math.abs(normalizedRadius - 1) <= normalizedTolerance;
};

export const hitTestAnnotationAt = ({
  annotations,
  point,
  slice,
  imageRect,
  defaultFontSize = 12,
  tolerancePx = 8,
}) => {
  if (!point || !Array.isArray(annotations)) return null;
  const maxDimension = Math.max(imageRect?.width || 0, imageRect?.height || 0);
  const threshold = maxDimension ? Math.max(0.008, tolerancePx / maxDimension) : 0.015;
  const visible = annotations.filter(annotation => annotation.slice === slice);

  for (let index = visible.length - 1; index >= 0; index -= 1) {
    const annotation = visible[index];
    if (textLabelHit(annotation, point, imageRect, defaultFontSize, tolerancePx)) return annotation;
    if (annotation.type === 'text' || annotation.type === 'level') continue;
    if (annotation.points?.a && annotation.points?.b) {
      if (distanceToSegment(point, annotation.points.a, annotation.points.b) <= threshold) return annotation;
      continue;
    }
    if (!Array.isArray(annotation.points)) continue;
    const points = annotation.points;
    if (annotation.type === 'rect' && points.length >= 2) {
      if (rectangleEdgeHit(point, points[0], points[1], threshold)) return annotation;
      continue;
    }
    if (annotation.type === 'ellipse' && points.length >= 2) {
      if (ellipseEdgeHit(point, points[0], points[1], threshold)) return annotation;
      continue;
    }
    if (annotation.type === 'cobb' && points.length >= 4) {
      if (
        distanceToSegment(point, points[0], points[1]) <= threshold
        || distanceToSegment(point, points[2], points[3]) <= threshold
      ) return annotation;
      continue;
    }
    if (points.some(candidate => Math.hypot(point.x - candidate.x, point.y - candidate.y) <= threshold * 1.5)) {
      return annotation;
    }
    for (let pointIndex = 1; pointIndex < points.length; pointIndex += 1) {
      if (distanceToSegment(point, points[pointIndex - 1], points[pointIndex]) <= threshold) return annotation;
    }
  }
  return null;
};

const screenPoint = (point, imageRect) => ({
  x: point.x * (imageRect?.width || 1),
  y: point.y * (imageRect?.height || 1),
});

const pointInsideBox = (point, box, tolerancePx = 0) => (
  point.x >= box.boxX - tolerancePx
  && point.x <= box.boxX + box.boxWidth + tolerancePx
  && point.y >= box.boxY - tolerancePx
  && point.y <= box.boxY + box.boxHeight + tolerancePx
);

const hitTestMeasurementAt = ({ measurements, point, slice, imageRect, tolerancePx, defaultFontSize, measurementLabelFor }) => {
  const visible = Array.isArray(measurements)
    ? measurements.filter(measurement => measurement.slice === slice && measurement.a && measurement.b)
    : [];
  const target = screenPoint(point, imageRect);
  const width = imageRect?.width || 1;
  const height = imageRect?.height || 1;
  for (let index = visible.length - 1; index >= 0; index -= 1) {
    const measurement = visible[index];
    const a = screenPoint(measurement.a, imageRect);
    const b = screenPoint(measurement.b, imageRect);
    const endpointTolerance = Math.max(10, tolerancePx * 1.5);
    if (Math.hypot(target.x - a.x, target.y - a.y) <= endpointTolerance) {
      return { item: measurement, part: 'a' };
    }
    if (Math.hypot(target.x - b.x, target.y - b.y) <= endpointTolerance) {
      return { item: measurement, part: 'b' };
    }
    const label = typeof measurementLabelFor === 'function' ? String(measurementLabelFor(measurement) || '') : String(measurement.label || '');
    if (label) {
      const layout = linearMeasurementLabelLayout({
        a,
        b,
        label,
        fontSize: measurement.labelFontSize || defaultFontSize,
        viewportWidth: width,
        viewportHeight: height,
      });
      if (pointInsideBox(target, layout, tolerancePx / 2)) return { item: measurement, part: 'label' };
    }
    if (distanceToSegment(target, a, b) <= tolerancePx) return { item: measurement, part: 'body' };
  }
  return null;
};

const hitTestRoiAt = ({ rois, point, slice, imageRect, tolerancePx, defaultFontSize, roiLabelLinesFor }) => {
  const visible = Array.isArray(rois)
    ? rois.filter(roi => roi.slice === slice && Number.isFinite(roi.cx) && Number.isFinite(roi.cy))
    : [];
  const width = imageRect?.width || 1;
  const height = imageRect?.height || 1;
  const minDimension = Math.min(width, height);
  const target = screenPoint(point, imageRect);
  for (let index = visible.length - 1; index >= 0; index -= 1) {
    const roi = visible[index];
    const center = screenPoint({ x: roi.cx, y: roi.cy }, imageRect);
    const radius = Number.isFinite(roi.r)
      ? Math.abs(roi.r * minDimension)
      : Math.max(Math.abs(Number(roi.rx) || 0) * width, Math.abs(Number(roi.ry) || 0) * height);
    const resizeHandles = [
      { x: center.x + radius, y: center.y },
      { x: center.x - radius, y: center.y },
      { x: center.x, y: center.y - radius },
      { x: center.x, y: center.y + radius },
    ];
    if (radius > 0 && resizeHandles.some(handle => Math.hypot(target.x - handle.x, target.y - handle.y) <= tolerancePx)) {
      return { item: roi, part: 'radius' };
    }
    const labelLines = typeof roiLabelLinesFor === 'function' ? roiLabelLinesFor(roi) : [];
    if (Array.isArray(labelLines) && labelLines.length) {
      const fontSize = roi.labelFontSize || Math.max(9, defaultFontSize - 1);
      const labelLeft = Math.min(center.x + radius + 2, width - 44);
      const labelTop = Math.max(center.y - 9, 0);
      const labelWidth = Math.max(44, ...labelLines.map(line => String(line).length * fontSize * 0.62));
      const labelHeight = labelLines.length * fontSize * 1.05 + 4;
      if (pointInsideBox(target, {
        boxX: labelLeft,
        boxY: labelTop,
        boxWidth: labelWidth,
        boxHeight: labelHeight,
      }, tolerancePx / 2)) return { item: roi, part: 'label' };
    }
    if (radius > 0 && Math.abs(Math.hypot(target.x - center.x, target.y - center.y) - radius) <= tolerancePx) {
      return { item: roi, part: 'body' };
    }
  }
  return null;
};

export const hitTestViewportMarkupAt = ({
  annotations,
  measurements,
  rois,
  point,
  slice,
  imageRect,
  defaultFontSize = 12,
  tolerancePx = 8,
  measurementLabelFor = null,
  roiLabelLinesFor = null,
}) => {
  if (!point) return null;
  const annotationItems = Array.isArray(annotations) ? annotations : [];

  // Resolve candidates in reverse visual paint order: ROI DOM overlays are above
  // the SVG, measurements paint after annotations, and annotations retain their
  // own array order inside the SVG layer.
  const roi = hitTestRoiAt({ rois, point, slice, imageRect, tolerancePx, defaultFontSize, roiLabelLinesFor });
  if (roi) return { kind: 'roi', item: roi.item, part: roi.part };
  const measurement = hitTestMeasurementAt({ measurements, point, slice, imageRect, tolerancePx, defaultFontSize, measurementLabelFor });
  if (measurement) return { kind: 'measurement', item: measurement.item, part: measurement.part };
  const annotation = hitTestAnnotationAt({ annotations: annotationItems, point, slice, imageRect, defaultFontSize, tolerancePx });
  return annotation ? { kind: 'annotation', item: annotation } : null;
};

export const prepareViewportMarkupForDrag = (kind, item, roiStatsSubjectToken = null) => {
  if (!item) return { working: item, rollback: item };
  if (kind !== 'roi') return { working: item, rollback: item };
  return {
    working: {
      ...item,
      stats: null,
      statsSubjectToken: roiStatsSubjectToken || item.statsSubjectToken,
    },
    rollback: item,
  };
};

export const moveMeasurementEndpointTo = (measurement, endpoint, point) => {
  if (!measurement?.a || !measurement?.b || !['a', 'b'].includes(endpoint) || !point) return measurement;
  const nextPoint = {
    x: Math.max(0, Math.min(1, Number(point.x))),
    y: Math.max(0, Math.min(1, Number(point.y))),
  };
  if (!Number.isFinite(nextPoint.x) || !Number.isFinite(nextPoint.y)) return measurement;
  return { ...measurement, [endpoint]: nextPoint };
};

export const resizeRoiRadiusTo = (roi, point, imageRect, minimumRadiusPx = 4) => {
  if (!roi || !point || !Number.isFinite(roi.cx) || !Number.isFinite(roi.cy)) return roi;
  const width = Number(imageRect?.width);
  const height = Number(imageRect?.height);
  const pointX = Number(point.x);
  const pointY = Number(point.y);
  if (!(width > 0) || !(height > 0) || !Number.isFinite(pointX) || !Number.isFinite(pointY)) return roi;
  const maxRadiusPx = Math.min(
    roi.cx * width,
    (1 - roi.cx) * width,
    roi.cy * height,
    (1 - roi.cy) * height,
  );
  if (!(maxRadiusPx > 0)) return roi;
  const requestedRadiusPx = Math.hypot((pointX - roi.cx) * width, (pointY - roi.cy) * height);
  const safeMinimumPx = Math.min(Math.max(0, Number(minimumRadiusPx) || 0), maxRadiusPx);
  const radiusPx = Math.max(safeMinimumPx, Math.min(maxRadiusPx, requestedRadiusPx));
  const minDimension = Math.min(width, height);
  return {
    ...roi,
    r: radiusPx / minDimension,
    rx: radiusPx / width,
    ry: radiusPx / height,
  };
};

export const moveViewportMarkupBy = (kind, item, dx, dy) => {
  if (!item) return item;
  if (kind === 'annotation') return moveAnnotationBy(item, dx, dy);
  if (kind === 'measurement' && item.a && item.b) {
    const constrained = constrainedDragDelta({ points: { a: item.a, b: item.b } }, dx, dy);
    return {
      ...item,
      a: movePoint(item.a, constrained.dx, constrained.dy),
      b: movePoint(item.b, constrained.dx, constrained.dy),
    };
  }
  if (kind === 'roi' && Number.isFinite(item.cx) && Number.isFinite(item.cy)) {
    const radiusX = Math.max(0, Math.abs(Number(item.rx) || Number(item.r) || 0));
    const radiusY = Math.max(0, Math.abs(Number(item.ry) || Number(item.r) || 0));
    return {
      ...item,
      cx: Math.max(radiusX, Math.min(1 - radiusX, item.cx + dx)),
      cy: Math.max(radiusY, Math.min(1 - radiusY, item.cy + dy)),
    };
  }
  return item;
};

const MARKUP_COLOR_ALLOWLIST = new Set(['#00e5ff', '#66ff66', '#ffeb3b', '#ff9800', '#ff5252', '#e040fb', '#ffffff']);
const sanitizeMarkupText = (value) => Array.from(String(value || ''))
  .filter(character => {
    const code = character.charCodeAt(0);
    return code >= 32 && code !== 127;
  })
  .join('')
  .slice(0, 160);
const boundedAppearanceNumber = (value, min, max) => Math.max(min, Math.min(max, Number(value)));

export const applyMarkupAppearancePatch = (item, patch = {}) => {
  if (!item) return item;
  const next = { ...item };
  if (patch.lineWidth !== undefined && Number.isFinite(Number(patch.lineWidth))) {
    next.lineWidth = boundedAppearanceNumber(patch.lineWidth, 1, 6);
  }
  if (patch.fontSize !== undefined && Number.isFinite(Number(patch.fontSize))) {
    next.fontSize = boundedAppearanceNumber(patch.fontSize, 8, 24);
  }
  if (patch.labelFontSize !== undefined && Number.isFinite(Number(patch.labelFontSize))) {
    next.labelFontSize = boundedAppearanceNumber(patch.labelFontSize, 8, 24);
  }
  if (typeof patch.color === 'string' && MARKUP_COLOR_ALLOWLIST.has(patch.color.toLowerCase())) {
    next.color = patch.color.toLowerCase();
  }
  if (typeof patch.fontFamily === 'string' && /^[\w\s,.'"-]{1,80}$/.test(patch.fontFamily)) next.fontFamily = patch.fontFamily;
  if (typeof patch.showLabels === 'boolean') next.showLabels = patch.showLabels;
  if (typeof patch.text === 'string') next.text = sanitizeMarkupText(patch.text);
  if (typeof patch.label === 'string') next.label = sanitizeMarkupText(patch.label);
  return next;
};

const MARKUP_APPEARANCE_FIELDS = ['lineWidth', 'fontSize', 'labelFontSize', 'color', 'fontFamily', 'showLabels', 'text', 'label'];

export const restoreMarkupAppearanceSnapshot = (current, original) => {
  if (!current || !original) return current;
  const restored = { ...current };
  MARKUP_APPEARANCE_FIELDS.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(original, field)) restored[field] = original[field];
    else delete restored[field];
  });
  return restored;
};
